/**
 * Run hints (#233): short in-run banners that teach a mechanic the moment it
 * first matters, such as an enemy coming into view or a caster winding up.
 * Each beat is authored in game-content/data/run-hints.json (trigger plus
 * text); the host watches for its trigger and shows it to a character once,
 * at its version (`character_run_hints_seen`), so a reworded beat shows again.
 */

/** The moments the host watches for, per player. */
export const RUN_HINT_TRIGGERS = [
    /** An enemy is first in the player's view. */
    'enemy-in-view',
    /** An essence orb first drops for the player. */
    'essence-orb-dropped',
    /** The player first collects an essence orb. */
    'essence-orb-collected',
    /** An enemy in view first begins a windup that breaking its poise would cancel. */
    'enemy-cast-started',
    /** An enemy in view is first interrupted: staggered out of a windup. */
    'enemy-interrupted',
    /** An essence cache first opens for the player. */
    'essence-cache-opened',
] as const;
export type RunHintTrigger = typeof RUN_HINT_TRIGGERS[number];

export function isRunHintTrigger(value: unknown): value is RunHintTrigger {
    return typeof value === 'string' && (RUN_HINT_TRIGGERS as readonly string[]).includes(value);
}

/** One hint beat. */
export interface RunHintDefinition {
    /** Slug, unique in the file. */
    readonly id: string;
    /** Raise it when the text changes, so characters who saw the old one see it again. */
    readonly version: number;
    readonly trigger: RunHintTrigger;
    /**
     * What the banner says. `{key:<binding id>}` stands for the player's key
     * for that input (e.g. `{key:action-bar-1}`), filled in by the client.
     */
    readonly text: string;
    /** The commissions whose runs show it. Omitted: every run. */
    readonly commissionIds?: readonly string[];
}

/** A hint shown to one player (`player:hint`). */
export interface RunHintPayload {
    readonly hintId: string;
    readonly text: string;
}

/** The hint beats a character has seen, by id, at the version seen. */
export type SeenRunHints = Readonly<Record<string, number>>;

/** Matches the `{key:<binding id>}` tokens in a hint's text; the id is group 1. */
export const RUN_HINT_KEY_TOKEN = /\{key:([a-z0-9-]+)\}/g;

const HINT_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Validates run-hints.json: `{ "hints": [...] }`. Returns the valid beats and
 * a problem for each invalid one, which is left out.
 */
export function parseRunHintLibrary(value: unknown): { hints: RunHintDefinition[]; problems: string[] } {
    const entries: unknown = (value as { hints?: unknown } | null)?.hints;
    if (!Array.isArray(entries)) {
        return { hints: [], problems: ['"hints" must be an array'] };
    }
    const hints: RunHintDefinition[] = [];
    const problems: string[] = [];
    const ids = new Set<string>();
    for (const [index, entry] of (entries as unknown[]).entries()) {
        const hint = entry as Partial<Record<keyof RunHintDefinition, unknown>> | null;
        const label = typeof hint?.id === 'string' ? `"${hint.id}"` : `#${index}`;
        if (typeof hint?.id !== 'string' || !HINT_ID_PATTERN.test(hint.id)) {
            problems.push(`hint ${label}: "id" must be a slug`);
        } else if (ids.has(hint.id)) {
            problems.push(`hint ${label}: its id is used by another hint`);
        } else if (!Number.isInteger(hint.version) || (hint.version as number) < 1) {
            problems.push(`hint ${label}: "version" must be a positive integer`);
        } else if (!isRunHintTrigger(hint.trigger)) {
            problems.push(`hint ${label}: "trigger" must be one of ${RUN_HINT_TRIGGERS.join(', ')}`);
        } else if (typeof hint.text !== 'string' || hint.text.trim() === '') {
            problems.push(`hint ${label}: "text" must be non-empty`);
        } else if (hint.commissionIds !== undefined
            && (!Array.isArray(hint.commissionIds) || !hint.commissionIds.every((id) => typeof id === 'string' && id !== ''))) {
            problems.push(`hint ${label}: "commissionIds" must be a list of commission ids`);
        } else {
            ids.add(hint.id);
            hints.push({
                id: hint.id,
                version: hint.version as number,
                trigger: hint.trigger,
                text: hint.text,
                ...(hint.commissionIds !== undefined ? { commissionIds: [...(hint.commissionIds as string[])] } : {}),
            });
        }
    }
    return { hints, problems };
}

/** The beats a run shows: those for its commission, and those for every run. */
export function listRunHints(hints: readonly RunHintDefinition[], commissionId: string | null): RunHintDefinition[] {
    return hints.filter((hint) => hint.commissionIds === undefined
        || (commissionId !== null && hint.commissionIds.includes(commissionId)));
}
